import React from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useMediaQuery } from "react-responsive";

type PaginationProps = {
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  currentPage: number;
};

const pageClass = (active: boolean) =>
  `inline-flex items-center justify-center min-w-[32px] h-8 px-2 rounded text-[13px] font-semibold tabular-nums transition-colors ${
    active
      ? "bg-primary text-white shadow-xs"
      : "text-ink-2 hover:bg-surface-hover border border-transparent hover:border-line"
  }`;

const arrowClass =
  "inline-flex items-center justify-center w-8 h-8 rounded border border-line text-ink-2 bg-surface hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors";

const PaginationNew: React.FC<PaginationProps> = ({ setCurrentPage, totalPages, currentPage }) => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const maxVisiblePages = isMobile ? 3 : 3;

  const getVisiblePages = () => {
    let start = Math.max(currentPage - Math.floor(maxVisiblePages / 2), 1);
    let end = start + maxVisiblePages - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxVisiblePages + 1, 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  return (
    <nav className="flex items-center gap-1" aria-label="Pagination">
      <button
        onClick={() => setCurrentPage(currentPage - 1)}
        disabled={currentPage === 1}
        className={arrowClass}
        aria-label="Previous page"
      >
        <FiChevronLeft />
      </button>

      {visiblePages[0] > 1 && (
        <>
          <button onClick={() => setCurrentPage(1)} className={pageClass(currentPage === 1)}>
            1
          </button>
          {visiblePages[0] > 2 && <span className="px-1 text-ink-4">…</span>}
        </>
      )}

      {visiblePages.map((page) => (
        <button
          key={page}
          onClick={() => setCurrentPage(page)}
          className={pageClass(currentPage === page)}
          aria-current={currentPage === page ? "page" : undefined}
        >
          {page}
        </button>
      ))}

      {visiblePages[visiblePages.length - 1] < totalPages && (
        <>
          {visiblePages[visiblePages.length - 1] < totalPages - 1 && <span className="px-1 text-ink-4">…</span>}
          <button onClick={() => setCurrentPage(totalPages)} className={pageClass(currentPage === totalPages)}>
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => setCurrentPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={arrowClass}
        aria-label="Next page"
      >
        <FiChevronRight />
      </button>
    </nav>
  );
};

export default PaginationNew;
